import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  PanelCard,
  TextField,
  Select,
  Switch,
  Checkbox,
  Button,
  Chip,
  Alert,
  Icon,
  GLPGrid,
} from '@glplus/glpweb-dsl';
import { vendorApiClient, type SubmitApplicationInput, type VendorApplication } from '../lib/vendorApiClient';
import { useVendor } from '../lib/CapabilitiesContext';
import { STORE_TYPES, STORE_TYPE_OTHER } from '../lib/storeTypes';
import {
  CONTACT_ROLES,
  LOCATION_COUNTS,
  SOCIAL_PLATFORMS,
  POS_SYSTEMS,
  PRODUCT_COUNTS,
  INTERESTS,
  INTEREST_OTHER,
  WEEK_DAYS,
} from '../lib/applyOptions';

/**
 * §1B "Apply / Access request" (Figma 304:4) — the vendor application form.
 * A single long page of accent-colored PanelCard sections (Business · Location &
 * hours · Systems & eligibility · Your Interests · Review) with a sticky stepper
 * that tracks the section in view. Writes the full businessInfo v2 contract.
 *
 * Doubles as the §1C "Edit application" surface: pass `existing` to prefill from
 * a submitted application and PATCH instead of POST.
 */

const GREEN = '#25862D';
const NAVY = '#343F5B';
const ORANGE = '#F67A1A';
const TEAL = '#07BDC4';
const BLUE = '#2563EB';

interface SocialRow {
  platform: string;
  handle: string;
}
interface DayState {
  open: string;
  close: string;
  closed: boolean;
}

function initHours(existing?: VendorApplication): Record<string, DayState> {
  const src = (existing?.hours ?? {}) as Record<string, { open?: string; close?: string; closed?: boolean }>;
  const out: Record<string, DayState> = {};
  for (const d of WEEK_DAYS) {
    const h = src[d.slug];
    out[d.slug] = {
      open: h?.open ?? '',
      close: h?.close ?? '',
      closed: h?.closed ?? (!h && d.slug === 'sunday'),
    };
  }
  return out;
}

export default function ApplyForm({ existing, onCancel }: { existing?: VendorApplication; onCancel?: () => void } = {}) {
  const { t } = useTranslation();
  const { refresh } = useVendor();
  const editing = !!existing;
  const bi = existing?.businessInfo;

  // ── Business ──
  const [businessName, setBusinessName] = useState(bi?.businessName ?? '');
  const [businessType, setBusinessType] = useState(bi?.businessType ?? '');
  const [businessTypeOther, setBusinessTypeOther] = useState(bi?.businessTypeOther ?? '');
  const [contactName, setContactName] = useState(bi?.contactName ?? '');
  const [role, setRole] = useState(bi?.role ?? 'owner');
  const [contactPhone, setContactPhone] = useState(bi?.contactPhone ?? '');
  const [website, setWebsite] = useState(bi?.website ?? '');
  const [socials, setSocials] = useState<SocialRow[]>(
    bi?.socialProfiles?.length ? bi.socialProfiles.map((s) => ({ platform: s.platform, handle: s.handle })) : [{ platform: 'instagram', handle: '' }],
  );
  const [numberOfLocations, setNumberOfLocations] = useState(bi?.numberOfLocations ?? '1');

  // ── Location & hours ──
  const [line1, setLine1] = useState(bi?.address?.line1 ?? '');
  const [line2, setLine2] = useState(bi?.address?.line2 ?? '');
  const [city, setCity] = useState(bi?.address?.city ?? '');
  const [state, setState] = useState(bi?.address?.state ?? '');
  const [postalCode, setPostalCode] = useState(bi?.address?.postalCode ?? '');
  const [hours, setHours] = useState<Record<string, DayState>>(() => initHours(existing));

  // ── Systems & eligibility ──
  const [snapAccepted, setSnapAccepted] = useState(bi?.snapAccepted ?? false);
  const [fnsNumber, setFnsNumber] = useState(bi?.fnsNumber ?? '');
  const [posSystem, setPosSystem] = useState(existing?.posSystem ?? '');

  // ── Your Interests ──
  const [approxProductCount, setApproxProductCount] = useState(bi?.approxProductCount ?? '');
  const [interests, setInterests] = useState<string[]>(bi?.interests ?? []);
  const [interestsOther, setInterestsOther] = useState(bi?.interestsOther ?? '');

  // ── Review ──
  const [terms, setTerms] = useState(editing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);

  const steps = [
    t('apply.steps.business'),
    t('apply.steps.location'),
    t('apply.steps.systems'),
    t('apply.steps.interests'),
    t('apply.steps.review'),
  ];

  const storeTypeOptions = useMemo(() => STORE_TYPES.map((s) => ({ value: s.slug, label: t(s.labelKey) })), [t]);
  const roleOptions = CONTACT_ROLES.map((o) => ({ value: o.slug, label: t(o.labelKey) }));
  const locationOptions = LOCATION_COUNTS.map((o) => ({ value: o.slug, label: t(o.labelKey) }));
  const platformOptions = SOCIAL_PLATFORMS.map((o) => ({ value: o.slug, label: t(o.labelKey) }));
  const posOptions = POS_SYSTEMS.map((o) => ({ value: o.slug, label: t(o.labelKey) }));
  const productCountOptions = [{ value: '', label: t('applyOpts.pos.none') }, ...PRODUCT_COUNTS.map((o) => ({ value: o.slug, label: t(o.labelKey) }))];

  const isOther = businessType === STORE_TYPE_OTHER;
  const wantsInterestOther = interests.includes(INTEREST_OTHER);

  useEffect(() => {
    const onScroll = () => {
      const tops = sectionRefs.current.map((el) => (el ? el.getBoundingClientRect().top : Infinity));
      let idx = 0;
      tops.forEach((top, i) => {
        if (top <= 140) idx = i;
      });
      setActive(idx);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (i: number) => {
    sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const canSubmit =
    businessName.trim() &&
    businessType &&
    (!isOther || businessTypeOther.trim()) &&
    contactName.trim() &&
    role &&
    contactPhone.trim() &&
    numberOfLocations &&
    line1.trim() &&
    city.trim() &&
    state.trim() &&
    postalCode.trim() &&
    terms &&
    !submitting;

  const buildHours = (): SubmitApplicationInput['hours'] => {
    const out: Record<string, { open?: string; close?: string; closed?: boolean }> = {};
    for (const d of WEEK_DAYS) {
      const h = hours[d.slug];
      if (h.closed) out[d.slug] = { closed: true };
      else if (h.open.trim() && h.close.trim()) out[d.slug] = { open: h.open.trim(), close: h.close.trim() };
    }
    return Object.keys(out).length ? out : undefined;
  };

  const toggleInterest = (slug: string) =>
    setInterests((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));

  const submit = async () => {
    if (!canSubmit) {
      setError(t('apply.validationError'));
      return;
    }
    setSubmitting(true);
    setError(null);
    const payload: SubmitApplicationInput = {
      businessName: businessName.trim(),
      businessType,
      businessTypeOther: isOther ? businessTypeOther.trim() : undefined,
      contactName: contactName.trim(),
      role,
      contactPhone: contactPhone.trim() || undefined,
      website: website.trim() || undefined,
      socialProfiles: socials.filter((s) => s.handle.trim()).map((s) => ({ platform: s.platform, handle: s.handle.trim() })),
      numberOfLocations,
      address: {
        line1: line1.trim(),
        line2: line2.trim() || undefined,
        city: city.trim(),
        state: state.trim(),
        postalCode: postalCode.trim(),
      },
      hours: buildHours(),
      snapAccepted,
      fnsNumber: snapAccepted ? fnsNumber.trim() || undefined : undefined,
      posSystem: posSystem || undefined,
      approxProductCount: approxProductCount || undefined,
      interests: interests.length ? interests : undefined,
      interestsOther: wantsInterestOther ? interestsOther.trim() || undefined : undefined,
    };
    try {
      // Pool-per-actor cutover (2026-09-17): NEW applications are created on
      // the public site (/vendors/apply), which provisions the vendor-pool
      // account this portal signs in with. This form is now edit-only.
      if (!(editing && existing)) throw new Error('Applications are submitted at grocerylistplus.com/vendors/apply');
      await vendorApiClient.editApplication(existing.applicationId, payload);
      refresh();
      onCancel?.();
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {editing ? t('apply.editTitle') : t('apply.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('apply.intro')}
        </Typography>
      </Box>

      {/* Sticky stepper */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          bgcolor: '#fff',
          border: '1px solid #e2ebe4',
          borderRadius: 3,
          px: 2,
          py: 1.25,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {steps.map((label, i) => (
          <Box
            key={label}
            component="button"
            type="button"
            onClick={() => scrollTo(i)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              px: 0.5,
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                bgcolor: i === active ? ORANGE : '#eef1f4',
                color: i === active ? '#fff' : '#8a93a3',
              }}
            >
              {i + 1}
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: i === active ? '#1f2733' : '#8a93a3', whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
            {i < steps.length - 1 && <Box sx={{ width: 16, height: 1, bgcolor: '#dde2e8', ml: 0.5 }} />}
          </Box>
        ))}
      </Box>

      {error && <Alert severity="error">{t('apply.error', { error })}</Alert>}

      {/* 1 — Business */}
      <SectionCard
        ref={(el) => { sectionRefs.current[0] = el; }}
        accent={GREEN}
        tint="#F3F8F3"
        title={t('apply.businessSection')}
        requiredLabel={t('apply.required')}
      >
        <GLPGrid container columns={12} spacing={2}>
          <GLPGrid size={{ xs: 12, sm: 6 }}>
            <TextField label={req(t('apply.businessName'))} ariaLabel={t('apply.businessName')} placeholder={t('apply.businessNamePlaceholder')} value={businessName} onChange={(e) => setBusinessName(e.target.value)} disabled={submitting} required />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, sm: 6 }}>
            <Select ariaLabel={t('apply.businessType')} label={req(t('apply.businessType'))} value={businessType} onChange={(_e, v) => setBusinessType(v)} options={storeTypeOptions} fullWidth />
          </GLPGrid>
          {isOther && (
            <GLPGrid size={12}>
              <TextField label={req(t('apply.businessTypeOther'))} ariaLabel={t('apply.businessTypeOther')} placeholder={t('apply.businessTypeOtherPlaceholder')} helperText={t('apply.manualReviewNote')} value={businessTypeOther} onChange={(e) => setBusinessTypeOther(e.target.value)} disabled={submitting} required />
            </GLPGrid>
          )}
          <GLPGrid size={{ xs: 12, sm: 7 }}>
            <TextField label={req(t('apply.contactName'))} ariaLabel={t('apply.contactName')} placeholder={t('apply.contactNamePlaceholder')} value={contactName} onChange={(e) => setContactName(e.target.value)} disabled={submitting} required />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, sm: 5 }}>
            <Select ariaLabel={t('apply.role')} label={req(t('apply.role'))} value={role} onChange={(_e, v) => setRole(v)} options={roleOptions} fullWidth />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, sm: 6 }}>
            <TextField label={req(t('apply.contactPhone'))} ariaLabel={t('apply.contactPhone')} type="tel" placeholder={t('apply.contactPhonePlaceholder')} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} disabled={submitting} required />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, sm: 6 }}>
            <TextField label={t('apply.website')} ariaLabel={t('apply.website')} placeholder={t('apply.websitePlaceholder')} value={website} onChange={(e) => setWebsite(e.target.value)} disabled={submitting} />
          </GLPGrid>

          <GLPGrid size={12}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#3a4350' }}>{t('apply.social')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{t('apply.socialHelp')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {socials.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box sx={{ width: 150, flexShrink: 0 }}>
                    <Select ariaLabel={t('apply.social')} value={s.platform} onChange={(_e, v) => setSocials((prev) => prev.map((p, j) => (j === i ? { ...p, platform: v } : p)))} options={platformOptions} fullWidth />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField ariaLabel={t('apply.social')} placeholder={t('apply.socialHandlePlaceholder')} value={s.handle} onChange={(e) => setSocials((prev) => prev.map((p, j) => (j === i ? { ...p, handle: e.target.value } : p)))} disabled={submitting} />
                  </Box>
                  <Box component="button" type="button" aria-label={t('apply.removeSocial')} onClick={() => setSocials((prev) => prev.filter((_, j) => j !== i))} sx={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#8a93a3', p: 0.5, display: 'inline-flex' }}>
                    <Icon name="close" size={18} ariaLabel="" />
                  </Box>
                </Box>
              ))}
              <Box component="button" type="button" onClick={() => setSocials((prev) => [...prev, { platform: 'instagram', handle: '' }])} sx={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', cursor: 'pointer', color: TEAL, fontWeight: 700, fontSize: 13, fontFamily: 'inherit', p: 0.5 }}>
                + {t('apply.addSocial')}
              </Box>
            </Box>
          </GLPGrid>

          <GLPGrid size={{ xs: 12, sm: 6 }}>
            <Select ariaLabel={t('apply.numberOfLocations')} label={req(t('apply.numberOfLocations'))} value={numberOfLocations} onChange={(_e, v) => setNumberOfLocations(v)} options={locationOptions} fullWidth />
          </GLPGrid>
        </GLPGrid>
      </SectionCard>

      {/* 2 — Location & hours */}
      <SectionCard
        ref={(el) => { sectionRefs.current[1] = el; }}
        accent={NAVY}
        tint="#EEF1F8"
        title={t('apply.locationSection')}
        requiredLabel={t('apply.required')}
      >
        <GLPGrid container columns={12} spacing={2}>
          <GLPGrid size={12}>
            <TextField label={req(t('apply.addressLine1'))} ariaLabel={t('apply.addressLine1')} placeholder={t('apply.addressLine1Placeholder')} value={line1} onChange={(e) => setLine1(e.target.value)} disabled={submitting} required />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, sm: 6 }}>
            <TextField label={t('apply.addressLine2')} ariaLabel={t('apply.addressLine2')} placeholder={t('apply.addressLine2Placeholder')} value={line2} onChange={(e) => setLine2(e.target.value)} disabled={submitting} />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, sm: 6 }}>
            <TextField label={req(t('apply.city'))} ariaLabel={t('apply.city')} value={city} onChange={(e) => setCity(e.target.value)} disabled={submitting} required />
          </GLPGrid>
          <GLPGrid size={{ xs: 6, sm: 6 }}>
            <TextField label={req(t('apply.state'))} ariaLabel={t('apply.state')} value={state} onChange={(e) => setState(e.target.value)} disabled={submitting} required />
          </GLPGrid>
          <GLPGrid size={{ xs: 6, sm: 6 }}>
            <TextField label={req(t('apply.postalCode'))} ariaLabel={t('apply.postalCode')} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} disabled={submitting} required />
          </GLPGrid>

          <GLPGrid size={12}>
            <Box sx={{ bgcolor: '#fff', border: '1px solid #e2ebe4', borderRadius: 2, p: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>{t('apply.hours')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>{t('apply.hoursHelp')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {WEEK_DAYS.map((d) => {
                  const h = hours[d.slug];
                  return (
                    <Box key={d.slug} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ width: 100, flexShrink: 0, fontWeight: 600 }}>{t(d.labelKey)}</Typography>
                      <Box sx={{ width: 120 }}>
                        <TextField ariaLabel={t('apply.openLabel', { day: t(d.labelKey) })} placeholder="9:00 AM" value={h.open} onChange={(e) => setHours((p) => ({ ...p, [d.slug]: { ...p[d.slug], open: e.target.value } }))} disabled={submitting || h.closed} size="small" />
                      </Box>
                      <Typography sx={{ color: '#8a93a3' }}>{t('apply.to')}</Typography>
                      <Box sx={{ width: 120 }}>
                        <TextField ariaLabel={t('apply.closeLabel', { day: t(d.labelKey) })} placeholder="7:00 PM" value={h.close} onChange={(e) => setHours((p) => ({ ...p, [d.slug]: { ...p[d.slug], close: e.target.value } }))} disabled={submitting || h.closed} size="small" />
                      </Box>
                      <Checkbox ariaLabel={t('apply.closedToggle', { day: t(d.labelKey) })} label={t('apply.closed')} checked={h.closed} onChange={(e) => setHours((p) => ({ ...p, [d.slug]: { ...p[d.slug], closed: e.target.checked } }))} disabled={submitting} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </GLPGrid>
        </GLPGrid>
      </SectionCard>

      {/* 3 — Systems & eligibility */}
      <SectionCard ref={(el) => { sectionRefs.current[2] = el; }} accent={ORANGE} tint="#FFF6EE" title={t('apply.systemsSection')}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{t('apply.acceptsSnap')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('apply.acceptsSnapHelp')}</Typography>
            </Box>
            <Switch ariaLabel={t('apply.acceptsSnap')} checked={snapAccepted} onChange={(_e, checked) => setSnapAccepted(checked)} disabled={submitting} color="success" />
          </Box>
          <TextField label={t('apply.fnsNumber')} ariaLabel={t('apply.fnsNumber')} placeholder={t('apply.fnsNumberPlaceholder')} helperText={t('apply.fnsNumberHelp')} value={fnsNumber} onChange={(e) => setFnsNumber(e.target.value)} disabled={submitting || !snapAccepted} />
          <Box>
            <Select ariaLabel={t('apply.posSystem')} label={t('apply.posSystem')} value={posSystem} onChange={(_e, v) => setPosSystem(v)} options={posOptions} fullWidth />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{t('apply.posSystemHelp')}</Typography>
          </Box>
        </Box>
      </SectionCard>

      {/* 4 — Your Interests */}
      <SectionCard ref={(el) => { sectionRefs.current[3] = el; }} accent={TEAL} tint="#E9FBFC" title={t('apply.interestsSection')}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ maxWidth: 360 }}>
            <Select ariaLabel={t('apply.approxProductCount')} label={t('apply.approxProductCount')} value={approxProductCount} onChange={(_e, v) => setApproxProductCount(v)} options={productCountOptions} fullWidth />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{t('apply.interestsLabel')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{t('apply.interestsHelp')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {INTERESTS.map((o) => {
                const on = interests.includes(o.slug);
                return (
                  <Chip
                    key={o.slug}
                    label={t(o.labelKey)}
                    clickable
                    onClick={() => toggleInterest(o.slug)}
                    icon={on ? 'check' : undefined}
                    backgroundColor={on ? '#FFF1E3' : '#fff'}
                    textColor={on ? '#C2410C' : '#5b6573'}
                    border={`1px solid ${on ? '#F6A06A' : '#dde2e8'}`}
                    fontSize={13}
                  />
                );
              })}
            </Box>
          </Box>
          {wantsInterestOther && (
            <TextField label={t('apply.interestsOther')} ariaLabel={t('apply.interestsOther')} placeholder={t('apply.interestsOtherPlaceholder')} helperText={t('apply.interestsOtherHelp')} value={interestsOther} onChange={(e) => setInterestsOther(e.target.value)} disabled={submitting} />
          )}
        </Box>
      </SectionCard>

      {/* 5 — Review & submit */}
      <SectionCard ref={(el) => { sectionRefs.current[4] = el; }} accent={BLUE} tint="#fff" title={t('apply.reviewSection')}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" title={t('apply.whatNextTitle')}>
            {t('apply.whatNext')}
          </Alert>
          <Checkbox ariaLabel={t('apply.terms')} label={t('apply.terms')} checked={terms} onChange={(e) => setTerms(e.target.checked)} disabled={submitting} color="success" />
        </Box>
      </SectionCard>

      {/* Footer actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {editing ? (
          <Button ariaLabel={t('apply.cancelEdit')} buttonText={t('apply.cancelEdit')} variant="outlinedGreen" onClick={() => onCancel?.()} disabled={submitting} />
        ) : (
          <span />
        )}
        <Box sx={{ ml: 'auto' }}>
          <Button
            ariaLabel={editing ? t('apply.saveEdits') : t('apply.submit')}
            buttonText={submitting ? t('apply.submitting') : editing ? t('apply.saveEdits') : t('apply.submit')}
            variant="contained"
            onClick={submit}
            disabled={!canSubmit}
          />
        </Box>
      </Box>
    </Box>
  );
}

/** A required field's label gets a trailing red asterisk. */
function req(label: string): string {
  return `${label} *`;
}

interface SectionCardProps {
  accent: string;
  tint: string;
  title: string;
  requiredLabel?: string;
  children: React.ReactNode;
}
const SectionCard = ({ ref, accent, tint, title, requiredLabel, children }: SectionCardProps & { ref?: (el: HTMLDivElement | null) => void }) => (
  <Box ref={ref} sx={{ scrollMarginTop: 90 }}>
    <PanelCard accent={accent} sx={{ height: 'auto', bgcolor: tint }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: accent }}>
          {title}
        </Typography>
        {requiredLabel && <Chip label={requiredLabel} size="small" backgroundColor="#FFE4E4" textColor="#BD001F" fontSize={11} />}
      </Box>
      {children}
    </PanelCard>
  </Box>
);
